import moment from 'moment'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

// UI
import { Box } from '@chakra-ui/react'

// Component
import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'

//API
import useUser from '@/features/users/useUser'

//State

//Constant / Helper
import { getOfferListCommonColumns } from '@/features/offers/constants/offerColumns'

import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'
import { authSelector } from '@/stores/auth/authSlice'
import { camelToWords, numberWithCommas } from '@/utils/stringHelper'
import { MaintenanceSummaryStatus, OfferQuotationStatus } from '../constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { RawTextCell } from '@/components/Table/components/Cell'
import MaintenanceSummaryListHeader from '../components/List/MaintenanceSummaryListHeader'
import OfferListStatusActionMenu from './OfferListPage/OfferListStatusActionMenu'

import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { useCostCenter } from '@/features/costCenters/useCostCenter'
import { useGCKCode } from '@/features/GCKCodes/useGCKCode'
import { useReason } from '@/features/reasons/useReason.js'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { UserEditRight, UserRole } from '@/features/users/constants'
import { OfferBookingStatus } from '@eapproval/offer-shared'
import useOfferList from '../hooks/useOfferList'

const MaintenanceSummaryPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const salesResponsible = searchParams.get('salesResponsible')
  const projectEndDate = searchParams.get('projectEndDate')

  const initialFilter =
    salesResponsible && projectEndDate
      ? {
          columnFilters: [
            { id: 'salesResponsible', value: salesResponsible },
            { id: 'projectEndDate', value: projectEndDate },
          ],
        }
      : {}
  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerMaintenanceSummary`, {})

  const query = convertTableStateToQuery(storedTableState)

  const columnHelper = createColumnHelper()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { useCostCenterOptions } = useCostCenter()
  const { useGCKCodeOptions } = useGCKCode()
  const { useFetchAllUsers, useUserOptions } = useUser()
  const { useFetchMaintenanceSummary } = useOfferList()
  const { useSystemVariableOptions } = useSystemVariable()
  const { useFetchReasonsByType } = useReason()

  const { data: reasonsData } = useFetchReasonsByType('maintenance')
  const lostReasonsOptions = reasonsData?.results || []

  // Hooks

  const { data: offersResponse, isRefetching } = useFetchMaintenanceSummary(query)

  //Options
  const { data: allUserOptions } = useUserOptions()
  const { data: salesUserOptions } = useUserOptions({
    labelKey: 'fullName',
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })
  const { data: PMOptions } = useUserOptions({
    contributorGroup: [
      ContributorGroupType.TECHNICAL_PROJECT_MANAGER,
      ContributorGroupType.SALES,
      ContributorGroupType.ENGINEER,
    ],
  })

  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active' })
  const { data: businessUnitOptions } = useSystemVariableOptions(SystemVariableType.BUSINESS_UNIT, { status: 'active' })
  const { data: costCenterOptions } = useCostCenterOptions({ status: 'active', labelType: 'detail' })
  const { data: GCKOptions } = useGCKCodeOptions({ status: 'active', labelType: 'detail' })

  const defaultColumns = [
    columnHelper.display({
      id: 'action',
      size: 80,
      enableHiding: false,
      enableColumnFilter: false,
      cell: ({ row: { original } }) => {
        return (
          <OfferListStatusActionMenu
            offerId={original?._id}
            offerStatus={original?.status}
            salesId={original?.salesId}
            projectName={original?.projectName}
            subSystemIndex={original?.subSystemIndex}
            showAdminButtons={
              currentUser.role === UserRole.ADMIN || currentUser?.editRights.includes(UserEditRight.WORKFLOW)
            }
            showCloneButton={false}
            showDetailButton={true}
            showLostButton={original?.status === 'Expired'}
            showRenewButton={
              original?.status !== MaintenanceSummaryStatus.RENEWAL_LOST &&
              original?.status !== MaintenanceSummaryStatus.RENEWED
            }
            showRenewalLostButton={
              original?.status !== MaintenanceSummaryStatus.RENEWAL_LOST &&
              original?.status !== MaintenanceSummaryStatus.RENEWED
            }
            showUndoRenewalLostButton={original?.status === MaintenanceSummaryStatus.RENEWAL_LOST}
          />
        )
      },
    }),
    ...getOfferListCommonColumns({
      salesUserOptions,
      creatorOptions: allUserOptions,
      teamOptions,
      businessUnitOptions,
      PMOptions,
      costCenterOptions,
      GCKOptions,
    }),
    columnHelper.accessor('newOrRenewal', {
      header: () => <span>New / Renewal</span>,

      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'New', value: 'new' },
          { label: 'Renewal', value: 'renewal' },
          { label: 'Renewal Of Discontinued', value: 'renewalOfDiscontinued' },
        ],
      },
    }),
    columnHelper.accessor('status', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(MaintenanceSummaryStatus).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
      },
    }),
    columnHelper.accessor('subSystemName', {
      header: () => <span>Sub System Project Name</span>,
      cell: RawTextCell,
    }),
    columnHelper.accessor('contractAmountPerMonth', {
      header: () => <span>Amount per month</span>,
      enableColumnFilter: false,
      cell: ({ row: { original } }) => {
        const startDate = original?.projectStartDate
        const endDate = original?.projectEndDate
        const totalPrice = original?.totalContractPrice
        if (!startDate || !endDate || !totalPrice) {
          return <TooltipCell text={'$0'} />
        }

        const numberOfDays = moment(endDate).diff(startDate, 'days', false)
        const numberOfMonths = ((numberOfDays + 1) / 365) * 12

        return <TooltipCell text={`$${numberWithCommas(totalPrice / numberOfMonths || 0)}`} />
      },
    }),
    columnHelper.accessor('financialYear', {
      header: () => <span>Since FY</span>,
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('upcomingQuotationNumber', {
      header: () => <span>Upcoming Quotation #</span>,
      cell: RawTextCell,
    }),
    columnHelper.accessor('maintenanceLostReason', {
      header: () => <span> Renewal Lost Reason</span>,
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values(lostReasonsOptions)?.map((reason) => ({
          label: camelToWords(reason?.name),
          value: reason?.name,
        })),
      },
    }),
    columnHelper.accessor('upcomingQuotationStatus', {
      meta: {
        filterVariant: FilterType.MULTI_SELECT,
        filterOptions: Object.values({ ...OfferQuotationStatus, ...OfferBookingStatus }).map((status) => {
          return {
            label: camelToWords(status),
            value: status,
          }
        }),
      },
    }),
    columnHelper.accessor('cRSPInUse', {
      header: () => <span>cRSP In Use</span>,

      meta: {
        filterVariant: FilterType.SELECT_YES_NO,
      },
    }),
    columnHelper.accessor('year', {
      cell: ({ row: { original } }) => {
        return <TooltipCell text={original?.year} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('remainMonth', {
      cell: ({ row: { original } }) => {
        const totalMonth = parseInt(original?.month) || 0
        return <TooltipCell text={totalMonth % 12} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('month', {
      cell: ({ row: { original } }) => {
        return <TooltipCell text={original?.month} />
      },
      meta: {
        filterVariant: FilterType.RANGE,
      },
    }),
    columnHelper.accessor('equipmentIds', {
      cell: ({ getValue }) => <TooltipCell text={getValue() ? getValue().join(', ') : ''} />,
    }),
    columnHelper.accessor('existingWBS', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('previousQuotationNumber', {
      cell: RawTextCell,
    }),
    columnHelper.accessor('maintenanceLostDetailedReason', {
      header: () => <span> Renewal Lost Reason - Detail</span>,
      cell: RawTextCell,
    }),
    columnHelper.accessor('isSOP', {
      header: () => <span>Security of Payment</span>,
      cell: ({ getValue }) => <TooltipCell text={getValue() ? 'Yes' : 'No'} />,
      meta: {
        filterVariant: FilterType.SELECT,
        filterOptions: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
    }),
  ]

  useEffect(() => {
    if (Object.keys(initialFilter).length > 0) {
      setStoredTableState((prev) => {
        return {
          ...prev,
          ...initialFilter,
        }
      })

      navigate(location.pathname, { replace: true })
    }
  }, [initialFilter])

  return (
    <>
      <Box shadow="md" borderWidth="1px" h="100%" w="auto" px="5">
        <MaintenanceSummaryListHeader offerCount={offersResponse?.count} tableState={storedTableState} />
        <Box py={5} w="full" h="90%" overflowY="scroll">
          <FullTable
            data={offersResponse?.data ? offersResponse.data : []}
            rowCount={offersResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            isLoading={isRefetching}
          />
        </Box>
      </Box>
    </>
  )
}

export default MaintenanceSummaryPage
